/**
 * @fileoverview ProgressTracker class for recording completed workouts and tracking user progress
 * Handles workout recording, statistics calculation, achievement tracking, and data export
 */

import StorageManager from './storage/storage-manager.js';

/**
 * @typedef {Object} CompletedWorkout
 * @property {string} id - Unique workout identifier
 * @property {string} programId - Program identifier
 * @property {string} sessionId - Session identifier
 * @property {string} completedAt - ISO date string when workout was completed
 * @property {number} duration - Actual workout duration in seconds
 * @property {boolean} completed - Whether workout was fully completed
 * @property {Object} [metadata] - Additional workout metadata
 */

/**
 * @typedef {Object} WorkoutStats
 * @property {number} totalWorkouts - Total number of completed workouts
 * @property {number} totalDuration - Total workout time in seconds
 * @property {number} averageDuration - Average workout duration in seconds
 * @property {number} currentStreak - Current consecutive workout days
 * @property {number} longestStreak - Longest consecutive workout days
 * @property {number} weeklyAverage - Average workouts per week
 * @property {Object} programStats - Statistics by program
 * @property {Date} firstWorkout - Date of first recorded workout
 * @property {Date} lastWorkout - Date of most recent workout
 */

/**
 * @typedef {Object} Achievement
 * @property {string} id - Unique achievement identifier
 * @property {string} name - Achievement name
 * @property {string} description - Achievement description
 * @property {string} category - Achievement category
 * @property {string} unlockedAt - ISO date string when unlocked
 * @property {Object} [metadata] - Additional achievement data
 */

/**
 * @typedef {Object} UserProgress
 * @property {WorkoutStats} stats - User workout statistics
 * @property {Achievement[]} achievements - Unlocked achievements
 * @property {string} currentProgram - Current active program ID
 * @property {number} currentSession - Current session index in program
 * @property {Object} preferences - User preferences
 */

/**
 * Manages user progress tracking, statistics, and achievements
 */
export class ProgressTracker {
    constructor(storageManager = null) {
        this.storage = storageManager || new StorageManager();
        this.initialized = false;
        this.achievementDefinitions = this.getAchievementDefinitions();
    }

    /**
     * Initialize the progress tracker
     * @returns {Promise<void>}
     */
    async init() {
        if (this.initialized) return;

        await this.storage.init();
        this.initialized = true;
    }

    /**
     * Record a completed workout
     * @param {Omit<CompletedWorkout, 'id'>} workout - Workout data without ID
     * @returns {Promise<string>} The generated workout ID
     */
    async recordWorkout(workout) {
        this.ensureInitialized();

        const workoutData = {
            ...workout,
            completedAt: workout.completedAt || new Date().toISOString(),
            completed: workout.completed !== false // Default to true unless explicitly false
        };

        const savedWorkout = await this.storage.saveWorkout(workoutData);

        // Check for new achievements after recording workout
        await this.checkAndUnlockAchievements();

        return savedWorkout.id;
    }

    /**
     * Get comprehensive user progress data
     * @returns {Promise<UserProgress>}
     */
    async getProgress() {
        this.ensureInitialized();

        const [stats, achievements, currentProgram, currentSession] = await Promise.all([
            this.calculateStats(),
            this.getUnlockedAchievements(),
            this.storage.getPreference('currentProgram'),
            this.storage.getPreference('currentSession', 0)
        ]);

        const preferences = await this.storage.getAllPreferences();

        return {
            stats,
            achievements,
            currentProgram,
            currentSession,
            preferences
        };
    }

    /**
     * Calculate comprehensive workout statistics
     * @returns {Promise<WorkoutStats>}
     */
    async calculateStats() {
        this.ensureInitialized();

        const workouts = await this.storage.getWorkouts();
        const completedWorkouts = workouts.filter(w => w.completed);

        if (completedWorkouts.length === 0) {
            return this.getEmptyStats();
        }

        // Sort workouts by completion date
        const sortedWorkouts = completedWorkouts.sort((a, b) =>
            new Date(a.completedAt) - new Date(b.completedAt)
        );

        const totalWorkouts = completedWorkouts.length;
        const totalDuration = completedWorkouts.reduce((sum, w) => sum + w.duration, 0);
        const averageDuration = totalDuration / totalWorkouts;

        const firstWorkout = new Date(sortedWorkouts[0].completedAt);
        const lastWorkout = new Date(sortedWorkouts[sortedWorkouts.length - 1].completedAt);

        // Calculate streaks
        const { currentStreak, longestStreak } = this.calculateStreaks(sortedWorkouts);

        // Calculate weekly average
        const weeklyAverage = this.calculateWeeklyAverage(sortedWorkouts);

        // Calculate program statistics
        const programStats = this.calculateProgramStats(completedWorkouts);

        return {
            totalWorkouts,
            totalDuration,
            averageDuration,
            currentStreak,
            longestStreak,
            weeklyAverage,
            programStats,
            firstWorkout,
            lastWorkout
        };
    }

    /**
     * Check for and unlock new achievements
     * @returns {Promise<Achievement[]>} Newly unlocked achievements
     */
    async checkAchievements() {
        return await this.checkAndUnlockAchievements();
    }

    /**
     * Export all user data for backup
     * @returns {Promise<Object>} Complete user data export
     */
    async exportData() {
        this.ensureInitialized();

        const data = await this.storage.exportData();
        const progress = await this.getProgress();

        return {
            ...data,
            progress,
            exportVersion: '1.0',
            appVersion: '1.0.0'
        };
    }

    /**
     * Import user data from backup
     * @param {Object} data - Exported data to import
     * @returns {Promise<void>}
     */
    async importData(data) {
        this.ensureInitialized();

        await this.storage.importData(data);

        // Re-check achievements after import
        await this.checkAndUnlockAchievements();
    }

    /**
     * Get workouts within a specific date range
     * @param {Date} startDate - Start date
     * @param {Date} endDate - End date
     * @returns {Promise<CompletedWorkout[]>}
     */
    async getWorkoutsByDateRange(startDate, endDate) {
        this.ensureInitialized();
        return await this.storage.getWorkoutsByDateRange(startDate, endDate);
    }

    /**
     * Get workouts for a specific program
     * @param {string} programId - Program identifier
     * @returns {Promise<CompletedWorkout[]>}
     */
    async getWorkoutsByProgram(programId) {
        this.ensureInitialized();
        return await this.storage.getWorkoutsByProgram(programId);
    }

    /**
     * Delete a workout record
     * @param {string} workoutId - Workout identifier
     * @returns {Promise<void>}
     */
    async deleteWorkout(workoutId) {
        this.ensureInitialized();
        await this.storage.deleteWorkout(workoutId);
    }

    /**
     * Set current program and session
     * @param {string} programId - Program identifier
     * @param {number} sessionIndex - Session index (0-based)
     * @returns {Promise<void>}
     */
    async setCurrentProgram(programId, sessionIndex = 0) {
        this.ensureInitialized();

        await Promise.all([
            this.storage.setPreference('currentProgram', programId),
            this.storage.setPreference('currentSession', sessionIndex)
        ]);
    }

    /**
     * Advance to next session in current program
     * @returns {Promise<number>} New session index
     */
    async advanceToNextSession() {
        this.ensureInitialized();

        const currentSession = await this.storage.getPreference('currentSession', 0);
        const newSession = currentSession + 1;

        await this.storage.setPreference('currentSession', newSession);
        return newSession;
    }

    // Private helper methods

    /**
     * Ensure the tracker is initialized
     * @private
     */
    ensureInitialized() {
        if (!this.initialized) {
            throw new Error('ProgressTracker not initialized. Call init() first.');
        }
    }

    /**
     * Get empty statistics object
     * @private
     * @returns {WorkoutStats}
     */
    getEmptyStats() {
        return {
            totalWorkouts: 0,
            totalDuration: 0,
            averageDuration: 0,
            currentStreak: 0,
            longestStreak: 0,
            weeklyAverage: 0,
            programStats: {},
            firstWorkout: null,
            lastWorkout: null
        };
    }

    /**
     * Calculate workout streaks
     * @private
     * @param {CompletedWorkout[]} sortedWorkouts - Workouts sorted by date
     * @returns {Object} Current and longest streak
     */
    calculateStreaks(sortedWorkouts) {
        if (sortedWorkouts.length === 0) {
            return { currentStreak: 0, longestStreak: 0 };
        }

        // Group workouts by date (only date part, not time)
        const workoutDates = new Set();
        sortedWorkouts.forEach(workout => {
            const date = new Date(workout.completedAt).toDateString();
            workoutDates.add(date);
        });

        const uniqueDates = Array.from(workoutDates).sort((a, b) => new Date(a) - new Date(b));

        if (uniqueDates.length === 1) {
            return { currentStreak: 1, longestStreak: 1 };
        }

        let longestStreak = 1;
        let tempStreak = 1;

        // Calculate longest streak
        for (let i = 1; i < uniqueDates.length; i++) {
            const prevDate = new Date(uniqueDates[i - 1]);
            const currDate = new Date(uniqueDates[i]);
            const dayDiff = Math.round((currDate - prevDate) / (1000 * 60 * 60 * 24));

            if (dayDiff === 1) {
                tempStreak++;
            } else {
                longestStreak = Math.max(longestStreak, tempStreak);
                tempStreak = 1;
            }
        }

        longestStreak = Math.max(longestStreak, tempStreak);

        // Calculate current streak from most recent workout backwards
        let currentStreak = 1;
        for (let i = uniqueDates.length - 2; i >= 0; i--) {
            const currDate = new Date(uniqueDates[i + 1]);
            const prevDate = new Date(uniqueDates[i]);
            const dayDiff = Math.round((currDate - prevDate) / (1000 * 60 * 60 * 24));

            if (dayDiff === 1) {
                currentStreak++;
            } else {
                break;
            }
        }

        return { currentStreak, longestStreak };
    }

    /**
     * Calculate weekly average workouts
     * @private
     * @param {CompletedWorkout[]} sortedWorkouts - Workouts sorted by date
     * @returns {number} Average workouts per week
     */
    calculateWeeklyAverage(sortedWorkouts) {
        if (sortedWorkouts.length === 0) return 0;

        const firstDate = new Date(sortedWorkouts[0].completedAt);
        const lastDate = new Date(sortedWorkouts[sortedWorkouts.length - 1].completedAt);
        const daysDiff = (lastDate - firstDate) / (1000 * 60 * 60 * 24);
        const weeksDiff = Math.max(daysDiff / 7, 1); // At least 1 week

        return sortedWorkouts.length / weeksDiff;
    }

    /**
     * Calculate statistics by program
     * @private
     * @param {CompletedWorkout[]} workouts - All completed workouts
     * @returns {Object} Statistics grouped by program
     */
    calculateProgramStats(workouts) {
        const programStats = {};

        workouts.forEach(workout => {
            if (!programStats[workout.programId]) {
                programStats[workout.programId] = {
                    totalWorkouts: 0,
                    totalDuration: 0,
                    averageDuration: 0,
                    completionRate: 0
                };
            }

            const stats = programStats[workout.programId];
            stats.totalWorkouts++;
            stats.totalDuration += workout.duration;
        });

        // Calculate averages
        Object.values(programStats).forEach(stats => {
            stats.averageDuration = stats.totalDuration / stats.totalWorkouts;
            stats.completionRate = 1.0; // All workouts in this array are completed
        });

        return programStats;
    }

    /**
     * Check and unlock new achievements
     * @private
     * @returns {Promise<Achievement[]>} Newly unlocked achievements
     */
    async checkAndUnlockAchievements() {
        const stats = await this.calculateStats();
        const existingAchievements = await this.getUnlockedAchievements();
        const existingIds = new Set(existingAchievements.map(a => a.id));
        const newAchievements = [];

        for (const achievementDef of this.achievementDefinitions) {
            if (existingIds.has(achievementDef.id)) continue;

            if (await this.checkAchievementCondition(achievementDef, stats)) {
                const achievement = {
                    ...achievementDef,
                    unlockedAt: new Date().toISOString()
                };

                await this.storage.saveAchievement(achievement);
                newAchievements.push(achievement);
            }
        }

        return newAchievements;
    }

    /**
     * Check if achievement condition is met
     * @private
     * @param {Object} achievementDef - Achievement definition
     * @param {WorkoutStats} stats - Current user stats
     * @returns {Promise<boolean>}
     */
    async checkAchievementCondition(achievementDef, stats) {
        switch (achievementDef.id) {
            case 'first-workout':
                return stats.totalWorkouts >= 1;

            case 'week-warrior':
                return stats.totalWorkouts >= 7;

            case 'streak-starter':
                return stats.currentStreak >= 3;

            case 'streak-master':
                return stats.longestStreak >= 7;

            case 'time-keeper':
                return stats.totalDuration >= 3600; // 1 hour

            case 'marathon-trainer':
                return stats.totalDuration >= 36000; // 10 hours

            case 'consistency-king':
                return stats.weeklyAverage >= 3;

            case 'program-completer':
                // Check if user completed any full program
                const workouts = await this.storage.getWorkouts();
                const programCounts = {};
                workouts.forEach(w => {
                    programCounts[w.programId] = (programCounts[w.programId] || 0) + 1;
                });
                // Assuming programs have 3 sessions (could be made more dynamic)
                return Object.values(programCounts).some(count => count >= 3);

            default:
                return false;
        }
    }

    /**
     * Get all unlocked achievements
     * @private
     * @returns {Promise<Achievement[]>}
     */
    async getUnlockedAchievements() {
        return await this.storage.getAchievements();
    }

    /**
     * Get achievement definitions
     * @private
     * @returns {Array} Achievement definitions
     */
    getAchievementDefinitions() {
        return [
            {
                id: 'first-workout',
                name: 'First Steps',
                description: 'Complete your first workout',
                category: 'milestone'
            },
            {
                id: 'week-warrior',
                name: 'Week Warrior',
                description: 'Complete 7 workouts',
                category: 'milestone'
            },
            {
                id: 'streak-starter',
                name: 'Streak Starter',
                description: 'Maintain a 3-day workout streak',
                category: 'consistency'
            },
            {
                id: 'streak-master',
                name: 'Streak Master',
                description: 'Achieve a 7-day workout streak',
                category: 'consistency'
            },
            {
                id: 'time-keeper',
                name: 'Time Keeper',
                description: 'Accumulate 1 hour of total workout time',
                category: 'duration'
            },
            {
                id: 'marathon-trainer',
                name: 'Marathon Trainer',
                description: 'Accumulate 10 hours of total workout time',
                category: 'duration'
            },
            {
                id: 'consistency-king',
                name: 'Consistency King',
                description: 'Average 3+ workouts per week',
                category: 'consistency'
            },
            {
                id: 'program-completer',
                name: 'Program Completer',
                description: 'Complete an entire training program',
                category: 'milestone'
            }
        ];
    }
}

export default ProgressTracker;